import type {Message, TokenUsage} from './hooks/useChatMessageFormatting'
import {CONTEXT_SUMMARIZED_ALERT_MESSAGE, normalizeContextUsage} from './omicsContextUsage'
import type {ChatMessageRecord} from './services/chatApi'
import {filesChangedFields, type FilesChangedFields} from './filesChanged'

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown
  } catch {
    return undefined
  }
}

/** Unwrap JSON string or pass through values already decoded by fetch(). */
function unwrapJsonField(raw: unknown): unknown {
  if (typeof raw === 'string') {
    return safeJsonParse(raw)
  }
  return raw
}

function strField(v: unknown): string | undefined {
  if (v == null) return undefined
  const s = typeof v === 'string' ? v : String(v)
  const t = s.trim()
  return t || undefined
}

function normalizeTokenUsage(raw: unknown): TokenUsage | undefined {
  const v = unwrapJsonField(raw)
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const input = Number(o.input_tokens ?? o.inputTokens)
  const output = Number(o.output_tokens ?? o.outputTokens)
  const total = Number(o.total_tokens ?? o.totalTokens)
  const calls = Number(o.llm_calls ?? o.llmCalls ?? 0)
  if (!Number.isFinite(total) || total <= 0) return undefined
  return {
    input_tokens: Number.isFinite(input) ? input : 0,
    output_tokens: Number.isFinite(output) ? output : 0,
    total_tokens: total,
    llm_calls: Number.isFinite(calls) ? calls : 0,
  }
}

/** Normalize tools_used from API (string[], objects, JSON string, Dynamo shapes). */
function normalizeToolsUsed(raw: unknown): string[] | undefined {
  let v = unwrapJsonField(raw)
  if (typeof v === 'string') {
    const t = v.trim()
    if (!t) return undefined
    if (t.startsWith('[') || t.startsWith('{')) {
      v = safeJsonParse(t)
    } else if (t.includes(',')) {
      v = t.split(',').map((s) => s.trim()).filter(Boolean)
    } else {
      v = [t]
    }
  }
  if (!Array.isArray(v)) return undefined
  const out: string[] = []
  for (const item of v) {
    if (typeof item === 'string') {
      const s = item.trim()
      if (s) out.push(s)
    } else if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>
      const name = o.tool_name ?? o.toolName ?? o.name ?? o.tool ?? o.Tool
      const s = strField(name)
      if (s) out.push(s)
    }
  }
  const uniq = [...new Set(out)]
  return uniq.length ? uniq : undefined
}

function normalizeKbSources(raw: unknown): Message['kbSources'] {
  const v = unwrapJsonField(raw)
  if (!Array.isArray(v)) return undefined
  const out: NonNullable<Message['kbSources']> = []
  for (const row of v) {
    if (!row || typeof row !== 'object') continue
    const o = row as Record<string, unknown>
    const source = o.source ?? o.Source
    if (typeof source !== 'string' || !source.trim()) continue
    const rel = o.relevance ?? o.Relevance
    out.push({
      source: source.trim(),
      ...(typeof rel === 'number' && Number.isFinite(rel) ? {relevance: rel} : {}),
    })
  }
  return out.length ? out : undefined
}

function normalizeInternetSources(raw: unknown): Message['internetSources'] {
  const v = unwrapJsonField(raw)
  if (!Array.isArray(v)) return undefined
  const out: NonNullable<Message['internetSources']> = []
  for (const row of v) {
    if (!row || typeof row !== 'object') continue
    const o = row as Record<string, unknown>
    const url = o.url ?? o.URL
    if (typeof url !== 'string' || !url.trim()) continue
    const title = o.title ?? o.Title
    out.push({
      url: url.trim(),
      ...(typeof title === 'string' && title.trim() ? {title: title.trim()} : {}),
    })
  }
  return out.length ? out : undefined
}

function pickRowField(m: ChatMessageRecord, camel: keyof ChatMessageRecord, snake: string): unknown {
  const row = m as ChatMessageRecord & Record<string, unknown>
  const c = row[camel]
  if (c !== undefined && c !== null) return c
  return row[snake]
}

/** Fields restored from the Chat API / Dynamo for one assistant row. */
export interface PersistedRunRowMeta extends FilesChangedFields {
  tokenUsage?: TokenUsage
  contextUsage?: Message['contextUsage']
  contextCompacted?: boolean
  modelUsed?: string
  agentRuntimeLabel?: string
  toolsUsed?: string[]
  suggestedFollowups?: string[]
  kbSources?: Message['kbSources']
  internetSources?: Message['internetSources']
}

/** Parse persisted run metadata from one API message row (camelCase or snake_case). */
export function parsePersistedRunRow(m: ChatMessageRecord): PersistedRunRowMeta {
  const tokenUsage = normalizeTokenUsage(pickRowField(m, 'tokenUsage', 'token_usage'))
  const toolsUsed = normalizeToolsUsed(pickRowField(m, 'toolsUsed', 'tools_used'))
  const suggestedFollowups = normalizeToolsUsed(pickRowField(m, 'suggestedFollowups', 'suggested_followups'))
  const kbSources = normalizeKbSources(pickRowField(m, 'kbSources', 'kb_sources'))
  const internetSources = normalizeInternetSources(pickRowField(m, 'internetSources', 'internet_sources'))
  // Compacted rows (file-ops contract, section 3) add the total, the flag and per-folder entries.
  const fileChanges = filesChangedFields(m)
  const modelUsed = strField(pickRowField(m, 'modelUsed', 'model_used'))
  const agentRuntimeLabel = strField(pickRowField(m, 'agentRuntimeLabel', 'agent_runtime_label'))
  const contextUsage = normalizeContextUsage(pickRowField(m, 'contextUsage', 'context_usage'))
  const compactedRaw = pickRowField(m, 'contextCompacted', 'context_compacted')
  const contextCompacted =
    compactedRaw === true || compactedRaw === 'true' || compactedRaw === 1

  return {
    ...(tokenUsage ? {tokenUsage} : {}),
    ...(contextUsage ? {contextUsage} : {}),
    ...(contextCompacted ? {contextCompacted: true} : {}),
    ...(modelUsed ? {modelUsed} : {}),
    ...(agentRuntimeLabel ? {agentRuntimeLabel} : {}),
    ...(toolsUsed ? {toolsUsed} : {}),
    ...(suggestedFollowups ? {suggestedFollowups} : {}),
    ...(kbSources ? {kbSources} : {}),
    ...(internetSources ? {internetSources} : {}),
    ...fileChanges,
  }
}

function streamAlertFromPersistedStatus(m: ChatMessageRecord): Message['streamAlert'] | undefined {
  const status = (m.status ?? '').toLowerCase()
  if (status === 'interrupted') {
    if (m.content?.includes('stopped by user')) return undefined
    return {kind: 'cancel', message: 'Response stopped by user'}
  }
  const compactedRaw = pickRowField(m, 'contextCompacted', 'context_compacted')
  if (compactedRaw === true || compactedRaw === 'true' || compactedRaw === 1) {
    return {kind: 'info', message: CONTEXT_SUMMARIZED_ALERT_MESSAGE}
  }
  return undefined
}

/** Merge Chat API assistant row fields into an in-memory Message (history reload). Returns a new object so React and converters see updates. */
export function mergePersistedAssistantRunMetadata<T extends Message>(msg: T, m: ChatMessageRecord): T {
  const alert = streamAlertFromPersistedStatus(m)
  return {
    ...msg,
    ...parsePersistedRunRow(m),
    ...(alert ? {streamAlert: alert} : {}),
  } as T
}
